import { type CSSProperties, useMemo } from 'react';
import { type Active, type Over } from '@dnd-kit/core';
import { CSS as DnDCss } from '@dnd-kit/utilities';
import {
    isDroppableOverData,
    isDroppableOverDataDelimiterWithNode,
    isDroppableOverDataDelimiterWithoutNode,
} from '../isDroppableOverData';
import { isTreeRow } from '../isTreeRow';
import { type DroppableOverData, type TreeRow } from '../types';

type Options = {
    /** Сдвиг индексов */
    delta?: number;
    /** Размер ряда */
    size?: 's' | 'm';
    /** дополнительные стили */
    additionalStyle?: CSSProperties | undefined;
};

/*
 * Возвращает стили для текущего ряда
 * @param currentRow текущий ряд
 * @param active активный ряд (тот что двигают)
 * @param over область перетаскивания (область в которую нацелен текущий перетаскиваемый ряд)
 * @param options дополнительные настройки
 */
export function useDraggableRowStyle<Attributes = unknown>(
    currentRow: TreeRow<Attributes> | null | undefined,
    active: Active | null,
    over: Over | null,
    options: Options = {}
) {
    const { additionalStyle, delta = 0, size = 'm' } = options;

    const activeRow = active?.data.current;
    const overData = over?.data.current;
    const height = active?.rect.current?.translated?.height ?? (size === 's' ? 36 : 44);

    return useMemo<CSSProperties | undefined>(() => {
        // Если текущий ряд не определен, возвращаем дополнительные стили
        if (!currentRow) {
            return additionalStyle;
        }

        // Если активный ряд не является рядом для дерева
        // Если данные области перетаскивания не являются ожидаемыми данными для дерева
        // возвращаем дополнительные стили
        if (!isTreeRow(activeRow) || !isDroppableOverData(overData)) {
            return additionalStyle;
        }

        // Если текущий ряд двигается вниз
        if (isRowMoveDown(currentRow, overData, delta)) {
            if (activeRow.id === overData.node?.id) {
                return {
                    ...additionalStyle,
                    transform: DnDCss.Transform.toString({ x: 0, y: 0, scaleX: 1, scaleY: 1 }),
                };
            }

            return {
                ...additionalStyle,
                transform: DnDCss.Transform.toString({ x: 0, y: height / 2, scaleX: 1, scaleY: 1 }),
            };
        }

        // Если текущий ряд двигается вверх
        if (isRowMoveUp(currentRow, overData, delta)) {
            if (activeRow.id === overData.node?.id) {
                return {
                    ...additionalStyle,
                    transform: DnDCss.Transform.toString({ x: 0, y: 0, scaleX: 1, scaleY: 1 }),
                };
            }

            return {
                ...additionalStyle,
                transform: DnDCss.Transform.toString({ x: 0, y: (-1 * height) / 2, scaleX: 1, scaleY: 1 }),
            };
        }

        return additionalStyle;
    }, [activeRow, additionalStyle, currentRow, delta, height, overData]);
}

/*
 * Надо ли двигать текущий ряд вниз
 */
function isRowMoveDown(currentRow: TreeRow, overData: DroppableOverData, delta: number = 0) {
    return isDroppableOverDataDelimiterWithNode(overData) && currentRow.index + delta >= overData.node.index;
}

/*
 * Надо ли двигать текущий ряд вверх
 */
function isRowMoveUp(currentRow: TreeRow, overData: DroppableOverData, delta: number = 0) {
    return (
        (isDroppableOverDataDelimiterWithNode(overData) && currentRow.index + delta < overData.node.index) ||
        isDroppableOverDataDelimiterWithoutNode(overData)
    );
}
